<template>
  <Datepicker
    v-bind="fullConfig"
    :modelValue="internalValue"
    @internal-model-change="handleInternalValueChange"
    @update:modelValue="onInput"
    @open="handleOpen"
    @closed="handleClose"
    class="c-date-picker"
  >
    <template v-if="iconName" #input-icon>
      <CIcon :name="iconName" class="u-ml-2" />
    </template>

    <template v-if="$slots['left-sidebar']" #left-sidebar>
      <slot name="left-sidebar" />
    </template>

    <template #action-row="{ internalModelValue, selectDate }">
      <div class="action-row">
        <CButton height="xsmall" @click="selectDate" fontWeight="normal">{{
          confirmLabel
        }}</CButton>
      </div>
    </template>
  </Datepicker>
</template>

<script setup lang="ts">
  import Datepicker, { VueDatePickerProps } from '@vuepic/vue-datepicker';
  import '@vuepic/vue-datepicker/dist/main.css';
  import moment, { Moment } from 'moment';
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import i18n from '@/general/i18n';
  import TimeService from '@/general/services/TimeService';
  import { datepickerDateFormat,
           datepickerDateTimeFormat,
           datepickerTimeFormat } from '../../utils/dateTime';
  import CIcon from '../CIcon.vue';
  import CButton from '../buttons/CButton.vue';

  type Value = Array<Moment | null> | Moment | null;
  const i18nLocale = useI18n();

  const confirmLabel = computed(() => i18nLocale.t('general.confirm'));

  const defaultConfig = {
    minutesIncrement: 15,
    teleport: true,
    textInput: true,
    autoApply: true,
  };

  const iconName = computed(() => {
    if (props.icon) {
      return props.icon;
    }

    if (props.type === 'datetime') {
      return 'calendar-clock';
    }

    return null;
  });

  const props = withDefaults(
    defineProps<{
      type: string;
      modelValue: Value;
      placeholder?: string;
      config?: VueDatePickerProps;
      icon?: string;
    }>(),
    {
      config: () => {
        return {} as VueDatePickerProps;
      },
    },
  );

  const emit = defineEmits<{
    (e: 'update:modelValue', value: Value): void;
    (e: 'internal-value-change', value: Date | Array<Date>): void;
  }>();

  const showTimePanel = ref(false);
  const tempInternalValue = ref<Date | Array<Date> | null>(null);
  const isPickerOpen = ref(false);
  const wasValueSelected = ref(false);

  const internalValue = computed(() => {
    const m = moment.isMoment(props.modelValue) ? props.modelValue : null;
    switch (props.type) {
      case 'daterange':
        return Array.isArray(props.modelValue)
          ? props.modelValue?.map((m) => m?.toDate())
          : null;
      case 'time':
      case 'duration':
        return m ? { hours: m.hours(), minutes: m.minutes() } : null;
      default:
        return m?.toDate() ?? null;
    }
  });

  const format = computed(() => {
    switch (props.type) {
      case 'time':
      case 'duration':
        return datepickerTimeFormat();
      case 'datetime':
        return datepickerDateTimeFormat();
      default:
        return datepickerDateFormat();
    }
  });

  const defaultPlaceholderKey = computed(() => {
    switch (props.type) {
      case 'time':
        return 'datepicker.time';
      case 'duration':
        return 'datepicker.duration';
      case 'datetime':
        return 'datepicker.dateTime';
      default:
        return 'datepicker.date';
    }
  });

  watch(
    () => props.modelValue,
    () => {
      wasValueSelected.value = false;
    },
  );

  function handleInternalValueChange(value: Date | Array<Date>) {
    if (value && props.type === 'datetime') {
      tempInternalValue.value = value;
      wasValueSelected.value = true;
    }

    emit('internal-value-change', value);
  }

  const startTime = getStartTime(
    props.config.minutesIncrement || defaultConfig.minutesIncrement,
  );

  const fullConfig = computed(() => {
    return {
      ...defaultConfig,
      locale: i18n.global.locale.value,
      format: format.value,
      range: props.type === 'daterange',
      weekPicker: props.type === 'week',
      monthPicker: props.type === 'month',
      yearPicker: props.type === 'year',
      timePicker: props.type === 'time' || props.type === 'duration',
      enableTimePicker:
        props.type === 'datetime' || props.type === 'time' || props.type === 'duration',
      placeholder: `${
        props.placeholder || i18nLocale.t(defaultPlaceholderKey.value)
      } (${format.value.toLowerCase()})`,
      multiCalendars: props.type === 'daterange',
      startTime,
      autoApply: !(props.type === 'datetime'),
      timePickerInline: true,
      ...props.config,
    };
  });

  function onInput(value: Date | Array<Date> | null): void {
    if (value && props.type === 'datetime' && !showTimePanel.value) {
      showTimePanel.value = true;
    }
    emit('update:modelValue', transformValue(value));
  }

  function handleOpen() {
    isPickerOpen.value = true;
  }

  function handleClose() {
    isPickerOpen.value = false;

    if (props.type === 'datetime' && tempInternalValue.value && wasValueSelected.value) {
      emit('update:modelValue', transformValue(tempInternalValue.value));
    }

    tempInternalValue.value = null;
    showTimePanel.value = false;
    wasValueSelected.value = false;
  }

  function transformValue(source: Date | Array<Date> | null) {
    switch (props.type) {
      case 'daterange':
        if (!Array.isArray(source)) return [null, null];
        return source.map((date) => (date ? moment(date) : null));
      default:
        if (Array.isArray(source)) return null;
        return source ? moment(source) : null;
    }
  }

  function getStartTime(minutesIncrement: number | string) {
    const mi = Number(minutesIncrement);
    if (props.type === 'duration') return { hours: 0, minutes: 0 };
    const now = TimeService.now.value;
    const minutesSinceMidnight = now.hours() * 60 + now.minutes();
    const rounded = Math.round(minutesSinceMidnight / mi) * mi;
    return {
      hours: Math.floor(rounded / 60),
      minutes: rounded % 60,
    };
  }
</script>

<style lang="scss">
//  TODO apply new colors in vendor CSS
@import "@/shared/assets/sass/vendors/DatePicker";

.dp__active_date {
  background-color: $primary-600;
}

.dp__cell_today {
  border: 1px solid $primary-600;
  background-color: transparent;
}

.c-date-picker {
  &.dp__main {
    padding: 0;
  }

  .dp__input {
    font-size: 1rem;
    line-height: 1.125;
    padding: 0.5rem 2.25rem;
    overflow: hidden;
  }

  .dp__disabled {
    background-color: $input-bg-disabled;
    color: $input-fg-disabled;
    cursor: not-allowed;
  }

  .dp__icon {
    color: $neutral-800;
  }
}

.dp__input {
  font-size: 1rem;
  line-height: 1.125;
  padding: 0.5rem 2.25rem;
  overflow: hidden;
  border-radius: 0;
  border: none;
}

.dp__disabled {
  background-color: $input-bg-disabled;
  color: $input-fg-disabled;
  cursor: not-allowed;
}

.dp__icon {
  color: $neutral-800;
}

.dp__time_col > .dp__btn {
  display: none;
}

.dp__time_col > .dp__time_display.dp__time_display_inline {
  padding: 16px;
}

.dp__time_col {
  margin-bottom: 5px;
}

.dp__time_col.dp__time_col_reg_inline {
  padding-inline: 2px;
}

.dp__time_picker_inline_container {
  border-top: 1px solid #ddd;
  padding-top: 5px;
}

.dp__action_row {
  padding-top: 0px;
}

.action-row {
  display: flex;
  justify-content: center;
  width: 100%;
}

.action_btn_row {
  background-color: #53b3ea;
  border: none;
  border-radius: 3px;
  color: white;
  font-family: "Roboto", sans-serif;
}
</style>
